import React, { useMemo } from 'react'
import styled from '@emotion/styled'
import Link from 'next/link'
import { ChevronLeft } from '@emotion-icons/material/ChevronLeft'
import { Typography } from '@mui/material'
import { Colors, Shapes } from 'src/components/theme'
import { useParsedRouter } from 'src/layout/hooks/use-parsed-router'
import { APP_SECTIONS_ROUTES } from 'src/layout/utils/routing'

type MenuItemProps = {
  icon: typeof ChevronLeft
  text?: string
  url: string
  iconSize?: number
}

export const MenuItemWrapper = styled.div<{ active?: boolean }>`
  position: relative;
  height: 36px;
  padding: 0 ${Shapes.spacing[3]}px;
  color: ${(props) => (props.active ? Colors.blueDark : Colors.textNavItem)};
  background: ${(props) => (props.active ? Colors.bgWhite : 'rgba(255, 255, 255, 0.08)')};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${Shapes.spacing[2]}px;
  cursor: pointer;
  border-radius: ${Shapes.borderRadius}px;

  &:hover {
    background: ${(props) => (props.active ? Colors.bgWhite : 'rgba(255, 255, 255, 0.2)')};
  }
`

export const MenuItem = (props: MenuItemProps) => {
  const { icon, text, url, iconSize: iconSizeProp } = props
  const { query, router } = useParsedRouter()

  // Based on the menu item's URL, determine if it should be active by checking if its associated routes are in the current path
  const active = useMemo(() => {
    switch (url) {
      case '/':
        // Special case for the home page
        if (router.asPath === '/') {
          return true
        }
        return (
          APP_SECTIONS_ROUTES.mapExplorer.some((route) => router.asPath.includes(route)) &&
          !router.asPath.includes('/owner-reveal')
        )
      case '/reports':
        return APP_SECTIONS_ROUTES.reports.some((route) => router.asPath.includes(route))
      case '/tenant-finder':
        return APP_SECTIONS_ROUTES.tenantFinder.some((route) => router.asPath.includes(route))
      case '/map-builder':
        return APP_SECTIONS_ROUTES.mapBuilder.some((route) => router.asPath.includes(route))
      case '/owner-reveal':
        return APP_SECTIONS_ROUTES.ownerReveal.some((route) => router.asPath.includes(route))
      default:
        return false
    }
  }, [router.asPath])

  const iconSize = iconSizeProp ? iconSizeProp : text ? 16 : 18
  const iconElement = React.createElement(icon, { size: iconSize })

  const redir = {
    pathname: url,
    query,
  }

  return (
    <Link href={redir}>
      <MenuItemWrapper active={active}>
        {iconElement}
        {text && <Typography variant="title2">{text}</Typography>}
      </MenuItemWrapper>
    </Link>
  )
}
